/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * Pitch Deck Content — v2: Direct & grounded
 * ─────────────────────────────────────────────
 * Removed hype, buzzwords, and marketing-speak.
 * Focused on specifics, evidence, and plain language.
 */

const pitchDeck = {
  meta: {
    title: "Paxia.ai — Investor Pitch",
    subtitle: "A trading platform with built-in risk controls and automation",
    date: "2025",
    accessCode: "paxia2025",
  },

  sections: [
    /* ── 0  HERO ── */
    {
      id: "hero",
      type: "hero",
      title: "Paxia.ai",
      subtitle: "A trading platform with built-in risk controls and automation",
      cta: "Scroll to explore ↓",
    },

    /* ── 1  PROBLEM ── */
    {
      id: "problem",
      type: "problem",
      label: "The Problem",
      title: "Retail trading is growing fast. Trader outcomes haven't improved.",
      lead: "There are roughly 20 million active retail traders globally, and more than 80% lose money. The causes are structural: platforms don't enforce risk rules, tools are scattered across multiple subscriptions, and automation requires programming skills most traders don't have.",
      painPoints: [
        {
          icon: "ShieldAlert",
          title: "Platforms don't enforce risk rules",
          body: "Traders set rules for themselves, but nothing in their platform prevents them from breaking those rules under pressure. Broker platforms are incentivised by trading volume, not trader outcomes.",
          tooltip: "ASIC and FCA data show 74–82% of retail CFD traders lose money. Broker revenue is tied to spread/commission volume, not to whether the trader profits.",
        },
        {
          icon: "Monitor",
          title: "Fragmented across multiple platforms",
          body: "Active traders commonly use 2–3 broker platforms plus separate tools for charting, news, journaling, and analysis. Managing P&L across accounts is manual.",
          tooltip: "Based on community surveys and prop-firm data: the average active trader uses 2.3 broker platforms and 4+ standalone tools. Each tool is a separate login, subscription, and data silo.",
        },
        {
          icon: "Brain",
          title: "Cognitive load leads to poor decisions",
          body: "Constant monitoring causes fatigue. Fatigue leads to impulsive trades, revenge trading after losses, and abandoning risk rules — the primary reasons retail traders lose money.",
          tooltip: "Barber & Odean (2001) found that overtrading reduces returns by 6.5% annually. Emotional decision-making is the single largest contributor to retail trading losses.",
        },
        {
          icon: "Code",
          title: "Automation requires coding skills",
          body: "Demand for algorithmic trading is high, but fewer than 5% of retail traders can write and maintain a trading bot. Hosting and monitoring bots adds $20–80/mo per account in VPS costs.",
          tooltip: "MQL5 marketplace has 30K+ Expert Advisors, indicating demand. But building, debugging, and deploying a custom bot requires MQL/Python proficiency plus server administration skills.",
        },
        {
          icon: "LayoutGrid",
          title: "Expensive, disconnected tool stack",
          body: "Charts, news feeds, bots, and analysis each require a separate subscription. Total cost is typically $150–400/mo, and switching between apps means missed trading signals.",
          tooltip: "Typical breakdown: TradingView ($30–60/mo), news/data feeds ($30–100/mo), journaling ($15–25/mo), VPS ($20–80/mo), indicator packs ($20–50/mo).",
        },
      ],
      stat: {
        value: "80%+",
        label: "of retail traders lose money",
        source: "ASIC, FCA, ESMA regulatory disclosures",
      },
    },

    /* ── 2  SOLUTION ── */
    {
      id: "solution",
      type: "solution",
      label: "The Solution",
      title: "One platform that enforces risk rules, connects multiple brokers, consolidates trading tools, and lets traders automate their strategies",
      capabilities: [
        {
          icon: "ShieldCheck",
          title: "Enforced Risk Controls",
          summary: "Risk rules are set once and enforced automatically — before, during, and after every trade. The trader cannot bypass them.",
          detail: "Loss caps, position sizing, quantity limits, and cooldown periods are defined once and applied consistently across all connected accounts. Every rule application is logged. There is no manual override — this is the core differentiator.",
          benefit: "Prevents the emotional overrides that cause most retail trading losses. Gives prop firms and compliance teams a verifiable audit trail.",
        },
        {
          icon: "Layers",
          title: "Multi-Broker Account Management",
          summary: "Manage MetaTrader, cTrader, and Binance accounts from a single interface. Additional brokers added over time.",
          detail: "Traders connect their existing broker accounts and manage them from one workspace. No need for separate logins, VPS instances, or manual P&L reconciliation. Infrastructure cost is less than half of running separate VPS instances.",
          benefit: "Reduces operational overhead and cost. Gives traders a single view of their positions and performance across all accounts.",
        },
        {
          icon: "BrainCircuit",
          title: "AI-Assisted Trading (Co-Pilot → Autopilot)",
          summary: "The AI applies user-defined rules and strategy logic to identify setups and, if enabled, execute trades — always within the risk controls.",
          detail: "Starts as a suggestion engine (Co-Pilot): the system identifies setups matching the trader's strategy and presents them for approval. If the trader enables Autopilot, the system executes trades autonomously — but risk controls always override. Every action is logged and auditable.",
          benefit: "Allows traders to run their strategy consistently without constant monitoring. The graduated approach (suggest → execute) builds trust and aligns with regulatory requirements.",
        },
        {
          icon: "LayoutGrid",
          title: "Consolidated Workspace",
          summary: "Charts, news, economic calendar, bots, backtesting, and journaling in one interface.",
          detail: "Instead of tabbing between TradingView, a news terminal, a spreadsheet journal, and a bot hosting platform, everything is in one workspace. Includes on-demand AI-generated technical and fundamental analysis.",
          benefit: "Fewer subscriptions, less context-switching, faster decision-making. All data is in the same context as the trading interface.",
        },
        {
          icon: "Bot",
          title: "No-Code Bot Builder",
          summary: "Describe a strategy in plain language → get a testable bot. No programming required.",
          detail: "Traders describe their strategy in natural language. The system generates a structured strategy that can be backtested and iterated on before going live. For traders who want more control, a visual builder is also available.",
          benefit: "Opens automation to the 95%+ of traders who can't code. Reduces strategy development from days/weeks to minutes.",
        },
      ],
    },

    /* ── 3  BUSINESS MODEL ── */
    {
      id: "business-model",
      type: "businessModel",
      label: "Business Model",
      title: "Tiered SaaS subscriptions with optional add-on modules",
      tiers: [
        {
          name: "Starter",
          price: "A$65/mo",
          features: ["1 broker account", "No-code bot builder", "1 live bot"],
          ideal: "Traders with a defined strategy who want to automate execution and enforce risk rules",
        },
        {
          name: "Pro",
          price: "A$150/mo",
          highlight: true,
          features: ["Everything in Starter", "AI Bot Builder", "3 live bots"],
          ideal: "Traders running multiple strategies who want AI-assisted bot creation",
        },
        {
          name: "Premium",
          price: "A$350/mo",
          features: ["Everything in Pro", "AI Assistant", "AI Autopilot"],
          ideal: "Traders who want end-to-end automation with AI-managed execution within their risk rules",
        },
      ],
      modules: [
        "Additional broker accounts & live bots",
        "AI assistant add-ons (prop-firm rules, daily due diligence)",
        "Backtest credits & priority queue",
        "News/sentiment & data packs",
        "Strategy template bundles",
        "Order flow (real volume) data & indicators",
        "Cross-account copy/allocation",
        "Team seats & collaboration",
      ],
      additionalRevenue: [
        {
          title: "Marketplace",
          body: "Traders can share or sell strategies and templates. Platform takes a percentage; creators earn rev-share.",
        },
        {
          title: "Prop-firm partnerships",
          body: "Pre-built rule packs for prop-firm challenges. Revenue share on referrals and completions.",
        },
        {
          title: "Enterprise licensing (future)",
          body: "The same platform deployed as a managed or white-label service for prop desks, brokers, and hedge funds.",
          tooltip: "This is a later-stage revenue channel. The same infrastructure can be private-labeled for institutional clients who need governed, auditable automated trading. Significantly higher ARPU than retail ($2K–10K/mo per seat).",
        },
      ],
    },

    /* ── 4  MARKET OPPORTUNITY ── */
    {
      id: "market",
      type: "market",
      label: "Market Opportunity",
      title: "Large addressable market with structural growth",
      tam: {
        value: "6.79M",
        accounts: 6_790_000,
        label: "active CFD accounts globally (Q4 2024)",
        tooltip: "Retail CFD accounts grew from ~3.5M to 6.79M in five years. Retail CFD now accounts for 14.1% of daily global FX turnover, up from 2.7%. Sources: BIS Triennial Survey, Investment Trends Global CFD & FX Report.",
      },
      adjacentMarket: {
        value: "40M–70M",
        label: "active crypto traders globally",
        tooltip: "Crypto traders have similar needs: automation, risk management, multi-exchange management. This is an expansion market, not part of initial go-to-market.",
      },
      sam: {
        value: "2.0M–3.0M",
        accounts: 2_500_000,
        label: "accounts reachable through our initial channels",
        tooltip: "Reachable through MetaTrader/cTrader ecosystems, prop-firm communities, trading education platforms, and affiliate networks. 30% assumed willingness to pay based on industry benchmarks.",
      },
      som: {
        value: "5–10K",
        accountsLow: 5_000,
        accountsHigh: 10_000,
        label: "paid subscriptions in 12–18 months (≈0.05–0.10% of SAM)",
        tooltip: "Based on CAC of A$80 with ~A$140K marketing budget in year 1. Acquisition channels: content marketing, affiliate/creator programs, prop-firm partnerships, targeted social ads. 0.05% conversion of SAM is conservative for a niche SaaS.",
      },
      revenueSizing: {
        currency: "A$",
        payerRate: 0.30,
        arpuScenarios: [55, 70, 110],
        payerRateNote: "TAM$/SAM$ assume 30% payer rate. SOM figures are paid subscribers — no payer‑rate discount.",
      },
    },

    /* ── 5  TRACTION ── */
    {
      id: "traction",
      type: "traction",
      label: "Traction & Validation",
      title: "Working product, validated with live trade execution",
      stats: [
        { value: "1,000+", label: "test trades executed across modules" },
        { value: "20%", label: "return over 2 months on demo account (AI Autopilot)" },
        { value: "1.94", label: "win ratio across 128 case-study trades" },
      ],
      builtToday: [
        "Working MVP with MetaTrader and cTrader integrations",
        "Real-time price feeds, historical data, trade execution, risk controls, journaling",
        "Charting and indicator library",
        "Customisable Trading Studio UI",
        "AI-assisted no-code bot builder with backtesting",
        "Automated trade management system (co-pilot and auto-pilot modes)",
        "AI-generated technical and fundamental analysis (on-demand and scheduled)",
        "News aggregation and economic calendar",
        "Scalable cloud infrastructure with automated deployment",
      ],
      caseStudy: {
        title: "Demo Account Case Study",
        body: "We ran an AI-managed strategy on a demo account over two months using live market data via cTrader and MetaTrader APIs. The system executed 128 trades with a win ratio of 1.94 and a total return of 20%. All trades were governed by the same risk controls available to users.",
        note: "Demo account credentials can be provided for independent verification.",
        tooltip: "This used a single strategy configuration. Results are from a demo environment with live market data — not hypothetical backtesting. Past performance on demo does not guarantee live results.",
        image: "ai-bot-casestudy",
      },
    },

    /* ── 6  UNIT ECONOMICS ── */
    {
      id: "unit-economics",
      type: "unitEconomics",
      label: "Unit Economics",
      title: "Projected unit economics at Month 12",
      headline: {
        subs: "1,224",
        mrr: "A$117.9K",
        grossMargin: "A$73.9K",
        cac: "A$80",
        payback: "1.4 mo",
        ltvCac: "12.5×",
        netMonthly: "+A$28.9K/m",
        cashPosition: "A$521K",
      },
      tiers: [
        { tier: "Starter", price: "A$65", infra: "A$27", gm: "A$38", retention: "12 mo", ltv: "A$456", ltvCac: "5.7×" },
        { tier: "Pro", price: "A$150", infra: "A$55", gm: "A$95", retention: "18 mo", ltv: "A$1,710", ltvCac: "21.4×" },
        { tier: "Premium", price: "A$350", infra: "A$90", gm: "A$260", retention: "24 mo", ltv: "A$6,240", ltvCac: "78.0×" },
      ],
      weightedArpu: "A$96.32",
      weightedGm: "A$60.37",
      weightedLtv: "A$987 (≈12.3× CAC)",
      tooltip: "These are projections, not actuals. LTV = GM$/user × average retention months. Weighted by projected M12 tier mix. CAC of A$80 assumes blended acquisition across content, affiliate, and paid channels. Retention assumptions are based on comparable trading SaaS benchmarks.",
    },

    /* ── 7  COMPETITIVE LANDSCAPE ── */
    {
      id: "competitive",
      type: "competitive",
      label: "Competitive Landscape",
      title: "No single existing platform covers the full stack",
      competitors: [
        { name: "Paxia.ai", noCode: true, aiAssist: true, backtest: true, marketplace: "roadmap", execution: true, multiAsset: "roadmap", agentic: true, mt5: "roadmap", fundamentals: true, news: true },
        { name: "Tradetron", noCode: true, aiAssist: false, backtest: true, marketplace: true, execution: true, multiAsset: "partial", agentic: false, mt5: false, fundamentals: false, news: false },
        { name: "Capitalise.ai", noCode: true, aiAssist: false, backtest: true, marketplace: false, execution: true, multiAsset: "partial", agentic: false, mt5: false, fundamentals: false, news: false },
        { name: "MetaTrader 5", noCode: false, aiAssist: false, backtest: true, marketplace: true, execution: true, multiAsset: true, agentic: false, mt5: true, fundamentals: "partial", news: true },
        { name: "QuantConnect", noCode: false, aiAssist: false, backtest: true, marketplace: "community", execution: true, multiAsset: true, agentic: false, mt5: false, fundamentals: true, news: "partial" },
        { name: "TradingView", noCode: false, aiAssist: false, backtest: true, marketplace: true, execution: true, multiAsset: true, agentic: false, mt5: false, fundamentals: true, news: true },
      ],
      features: [
        { key: "noCode", label: "No-code Bot Builder" },
        { key: "aiAssist", label: "AI Assist" },
        { key: "backtest", label: "Backtesting" },
        { key: "marketplace", label: "Marketplace" },
        { key: "execution", label: "Execution" },
        { key: "multiAsset", label: "Multi-asset" },
        { key: "agentic", label: "Automated Strategy Execution" },
        { key: "mt5", label: "MT5 Integration" },
        { key: "fundamentals", label: "Fundamentals" },
        { key: "news", label: "News/Calendar" },
      ],
      moat: [
        {
          title: "Enforced risk controls (hard to replicate)",
          body: "Pre-trade, in-trade, and post-trade controls applied across all connected accounts. Bypass-proof by design. Includes per-trade risk limits, max consecutive loss, daily drawdown, cooldowns, and time-of-day restrictions — all with audit trails.",
        },
        {
          title: "Automated strategy execution",
          body: "The system identifies setups matching the trader's rules, executes trades, and manages positions — all within defined risk guardrails. This is not a signal service; it's full lifecycle trade management.",
        },
        {
          title: "Broker integration breadth",
          body: "Live integrations with MT5, cTrader, and Binance. IG, IBKR, and FXCM in progress. Each integration includes a QA test harness and monitoring. This breadth takes time to build and is a compounding advantage.",
        },
        {
          title: "Marketplace network effects",
          body: "More strategy creators → more templates available → more users → more performance data → better ranking and discovery → attracts more creators. This flywheel strengthens over time.",
        },
        {
          title: "Switching costs",
          body: "Users accumulate accounts, risk rules, bot configurations, trade history, and analytics in one place. Migrating to a competitor means losing that integrated context.",
        },
        {
          title: "Cost advantage",
          body: "Less than 50% the cost of running equivalent VPS-based setups. Combined with SOC 2 readiness and AFSL alignment, this makes Paxia attractive as a partner to brokers and prop firms.",
        },
      ],
    },

    /* ── 8  TEAM ── */
    {
      id: "team",
      type: "team",
      label: "Team",
      title: "Combined experience across trading, engineering, and growth",
      members: [
        {
          name: "Ehsan Paksima",
          role: "Founder",
          photo: null,
          linkedin: "https://www.linkedin.com/in/ehsanpaksima/",
          highlights: [
            "15 years leading technology operations; delivered $7M+ in cost savings through automation and system re-architecture",
            "Built and managed distributed engineering teams (onshore and offshore)",
            "Active trader for 10+ years; uses MT4/5 and cTrader daily",
            "As Director of Technology at a SaaS startup, increased profit 130% in 14 months through new product development and OPEX reduction",
            "Solutions architect; led full-stack engineering teams across multiple companies",
          ],
        },
        {
          name: "Erfan Paksima",
          role: "Co-founder (Engineering)",
          photo: null,
          linkedin: "https://www.linkedin.com/in/erfan-paksima-670676147/",
          highlights: [
            "Senior full-stack developer and solutions architect",
            "Focused on backend systems, DevOps automation, and applied machine learning",
            "Helped multiple startups resolve scaling, data, and infrastructure bottlenecks",
            "10 years active trading across multiple markets and platforms",
            "Built CI/CD pipelines, monitoring systems, and cost-optimised cloud infrastructure",
          ],
        },
        {
          name: "Geoffrey Emerson",
          role: "CMO / CX",
          photo: null,
          linkedin: "https://www.linkedin.com/in/emersongeoffrey/",
          highlights: [
            "Worked with NSW Office of AI; runs PERCI.IO AI consultancy",
            "Specialises in digital audits, service design, and online marketing",
            "Award-winning Head of Digital with experience across fintech and SaaS",
            "Background in social search and content strategy",
            "Track record of brand growth through targeted digital campaigns",
          ],
        },
      ],
    },

    /* ── 9  ROADMAP ── */
    {
      id: "roadmap",
      type: "roadmap",
      label: "Roadmap",
      title: "Product roadmap: 12–18 months",
      phases: [
        {
          label: "Now (0–3 months)",
          subtitle: "Polish and prepare for paid users",
          items: [
            "UI polish and charting drawing tools",
            "Billing system: subscriptions and account management",
            "Security: SOC 2 preparation and penetration test scheduling",
            "Expand indicator library",
            "Add equities fundamental data",
          ],
          kpis: "50–100 pilot users, crash-free sessions >99%, Day-7 retention ≥50%",
        },
        {
          label: "Next (3–9 months)",
          subtitle: "Paid launch and marketplace",
          items: [
            "Strategy marketplace (share/sell/review strategies with rev share)",
            "Mobile app and education hub",
            "IG, IBKR, and FXCM broker integrations",
            "AI trade management v2: multi-strategy orchestration, event-driven hedging",
          ],
          kpis: "1–2K paid subscribers, +20–30% ARPU uplift from tier upgrades, churn <8%/mo",
        },
        {
          label: "Later (9–18 months)",
          subtitle: "Scale and enterprise",
          items: [
            "Marketplace payments, moderation, and curation",
            "Proprietary ML models for pattern recognition",
            "Prop-firm integration pilots and broker co-marketing",
            "Backtesting v2: tick-level data, walk-forward analysis, Monte Carlo simulation",
            "Portfolio dashboard, full mobile app, team roles/permissions",
          ],
          kpis: "6–10K paid subscribers, churn <5%/mo, gross margin ≥70%, 99.9% feed uptime",
        },
      ],
    },

    /* ── 10  RISKS ── */
    {
      id: "risks",
      type: "risks",
      label: "Dependencies & Risks",
      title: "Known risks with planned mitigations",
      items: [
        {
          risk: "Broker and prop-firm API variability",
          mitigation: "Abstraction layer per broker + automated sandbox testing before each release",
          tooltip: "Each broker's API has unique behaviours, rate limits, and error handling. Our adapter pattern isolates broker-specific logic so the core system doesn't change per integration.",
        },
        {
          risk: "Market data and backtest quality",
          mitigation: "Licensed data feeds with provenance tracking, drift monitoring, and vendor SLAs",
        },
        {
          risk: "Regulatory scope (AFSL / MDA)",
          mitigation: "Launching as non-discretionary (no Autopilot initially). Autopilot rolled out under a Corporate Authorised Representative arrangement. Own AFSL application within 12–18 months.",
          tooltip: "Autopilot constitutes a Managed Discretionary Account (MDA) under Australian law. We stage the rollout: non-discretionary first → CAR → own AFSL. We never hold client funds at any stage.",
        },
        {
          risk: "Security and data privacy",
          mitigation: "Secrets vaulting, least-privilege API tokens, 2FA, audit logging, external penetration testing, SOC 2 preparation",
        },
        {
          risk: "Client funds custody risk",
          mitigation: "Paxia never touches client funds. All orders route directly to the user's broker. This is an architectural decision, not a policy — the system has no mechanism to hold or transfer funds.",
          tooltip: "We are a SaaS overlay. The user's money stays with their regulated broker at all times. Paxia sends trade instructions via broker APIs; we never have access to deposits or withdrawals.",
        },
      ],
    },

    /* ── 11  THE ASK ── */
    {
      id: "ask",
      type: "ask",
      label: "The Ask",
      title: "Pre-Seed: A$700K (SAFE)",
      raising: "A$700K",
      instrument: "SAFE",
      runway: "18 months to general availability and 5–10K paying users",
      useOfFunds: [
        { label: "Product & Compliance", pct: 40, detail: "Complete the 12–18 month product roadmap" },
        { label: "Marketing & Growth", pct: 20, detail: "Creator/affiliate programs, prop-firm pilots, content and SEO, targeted social ads, attribution and analytics" },
        { label: "Regulatory & Licensing", pct: 20, detail: "AFSL/partner requirements, legal review, audits, governance framework" },
        { label: "Operations & Support", pct: 20, detail: "Customer support, cloud infrastructure, payment processing, admin" },
      ],
      hiringPlan: [
        { role: "Engineering", detail: "2 backend, 1 frontend, 1 integration engineer (offshore)" },
        { role: "Design", detail: "1 UI/UX designer (onshore)" },
        { role: "Growth", detail: "1 marketing hire" },
        { role: "Month 6", detail: "1 customer support + 1 business development / sales" },
      ],
    },
  ],
};

export default pitchDeck;
